import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { MapContainer, TileLayer, Marker, Tooltip, useMap, ZoomControl } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./App.css";
import Login from "./Login";
import ProfileChip from "./ProfileChip";
import * as XLSX from "xlsx";

const API = "/api/batteries";
const POLL_INTERVAL = 10000;

// ── Brand Colors ──

const COLORS = {
  green: "#38e0cf",
  yellow: "#e0d438",
  orange: "#fd7e14",
  red: "#dc3545",
  gray: "#898989",
};

// ── Helpers ──

function socColor(val) {
  if (val == null) return "gray";
  if (val >= 60) return "green";
  if (val >= 40) return "yellow";
  if (val >= 20) return "orange";
  return "red";
}

function sohColor(val) {
  if (val == null) return "gray";
  if (val >= 80) return "green";
  if (val >= 60) return "yellow";
  if (val >= 40) return "orange";
  return "red";
}

// ── Bucket helpers (used by multi-select filters) ──

function socBucket(v) {
  if (v == null) return null;
  if (v < 15) return "critical";
  if (v < 30) return "low";
  if (v < 60) return "mid";
  return "good";
}

function sohBucket(v) {
  if (v == null) return null;
  if (v < 40) return "critical";
  if (v < 60) return "low";
  if (v < 80) return "mid";
  return "good";
}

function tempBucket(v) {
  if (v == null) return null;
  if (v < 35) return "cool";
  if (v < 45) return "warm";
  if (v < 50) return "hot1";
  if (v < 55) return "hot2";
  return "critical";
}

function dischargeBucket(v) {
  if (v == null) return null;
  if (v < 5000) return "low";
  if (v < 20000) return "mid";
  if (v < 50000) return "high";
  return "veryhigh";
}

// Marker color by parameter (battery dashboard). Keep these aligned with the
// bucket helpers above so a filter on, say, SOC produces the matching map
// coloring under "auto".
function tempColor(val) {
  if (val == null) return "gray";
  if (val < 35) return "green";
  if (val < 45) return "yellow";
  if (val < 55) return "orange";
  return "red";
}

function dischargeColor(val) {
  if (val == null) return "gray";
  if (val < 5000)  return "green";
  if (val < 20000) return "yellow";
  if (val < 50000) return "orange";
  return "red";
}

const BATTERY_COLOR_BY = {
  soc:       { label: "SOC",            fn: (b) => socColor(b.soc) },
  soh:       { label: "SOH",            fn: (b) => sohColor(b.batt_soh) },
  temp:      { label: "Max Cell Temp",  fn: (b) => tempColor(b.max_cell_temp) },
  discharge: { label: "Total Discharge",fn: (b) => dischargeColor(b.total_dischg_pwr) },
};

const SOC_OPTIONS = [
  { value: "critical", label: "<15%" },
  { value: "low", label: "15–30%" },
  { value: "mid", label: "30–60%" },
  { value: "good", label: "≥60%" },
];

const SOH_OPTIONS = [
  { value: "critical", label: "<40%" },
  { value: "low", label: "40–60%" },
  { value: "mid", label: "60–80%" },
  { value: "good", label: "≥80%" },
];

const TEMP_OPTIONS = [
  { value: "cool", label: "<35°C" },
  { value: "warm", label: "35–45°C" },
  { value: "hot1", label: "45–50°C" },
  { value: "hot2", label: "50–55°C" },
  { value: "critical", label: "≥55°C" },
];

const DISCHARGE_OPTIONS = [
  { value: "low", label: "<5k kWh" },
  { value: "mid", label: "5–20k" },
  { value: "high", label: "20–50k" },
  { value: "veryhigh", label: "≥50k" },
];

function fmt(val, unit = "", decimals = 1) {
  if (val == null || val === undefined) return "N/A";
  return `${Number(val).toFixed(decimals)}${unit}`;
}

function fmtTime(epoch) {
  if (!epoch) return "N/A";
  const val = Number(epoch);
  // If value > 1e12 it's already milliseconds, otherwise seconds
  const ms = val > 1e12 ? val : val * 1000;
  return new Date(ms).toLocaleString();
}

function dedup(batteries) {
  const map = new Map();
  batteries.forEach((b) => {
    const key = String(b.battery_id).trim();
    map.set(key, { ...b, battery_id: key });
  });
  return Array.from(map.values());
}

function groupByLocation(batteries) {
  // groupKey -> { kind, label, station_name, truck, fleet, items[] }
  const groups = {};
  for (const b of batteries) {
    let key, kind, label;
    if (b.station_id) {
      key = `__station_${b.station_id}__`;
      kind = "station";
      label = b.station_name;
    } else if (b.associated_truck && b.associated_truck.toLowerCase() !== "unknown") {
      key = b.associated_truck;
      kind = "truck";
      label = b.associated_truck;
    } else {
      key = "__unlinked__";
      kind = "unlinked";
      label = "Unlinked (off-station)";
    }
    if (!groups[key]) {
      groups[key] = {
        kind,
        label,
        station_name: kind === "station" ? b.station_name : null,
        truck: kind === "truck" ? b.associated_truck : null,
        fleet: null,
        items: [],
      };
    }
    if (!groups[key].fleet && b.fleet_name) groups[key].fleet = b.fleet_name;
    groups[key].items.push(b);
  }
  return groups;
}

const GROUP_KIND_ORDER = { station: 0, truck: 1, unlinked: 2 };

function sortedGroupEntries(groups) {
  return Object.entries(groups).sort(([ak, a], [bk, b]) => {
    if (a.kind !== b.kind) return GROUP_KIND_ORDER[a.kind] - GROUP_KIND_ORDER[b.kind];
    if (a.kind === "station") return (a.station_name || "").localeCompare(b.station_name || "");
    if (a.kind === "truck") return (a.truck || "").localeCompare(b.truck || "");
    return ak.localeCompare(bk);
  });
}

const iconCache = {};

function createMarkerIcon(color, isSelected) {
  const key = `${color}-${isSelected}`;
  if (iconCache[key]) return iconCache[key];

  const cls = isSelected ? "selected" : "";
  const bg = COLORS[color] || COLORS.gray;

  iconCache[key] = L.divIcon({
    className: "custom-marker",
    html: `<div class="marker-dot ${cls}" style="background:${bg}"></div>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
  });

  return iconCache[key];
}

// Swap-station marker — visually distinct from battery dots so they read as
// fixed infrastructure rather than fleet movement. Hexagon shape, larger,
// teal fill with a lightning glyph.
let _stationIcon = null;
function createStationIcon() {
  if (_stationIcon) return _stationIcon;
  _stationIcon = L.divIcon({
    className: "station-marker",
    html: `
      <div class="station-hex">
        <svg viewBox="0 0 32 32" width="28" height="28" aria-hidden="true">
          <polygon points="16,1 30,9 30,23 16,31 2,23 2,9"
                   fill="#01363F" stroke="#2dc4b3" stroke-width="2"/>
          <path d="M18 7 L11 18 L15 18 L13 25 L21 13 L17 13 Z"
                fill="#2dc4b3"/>
        </svg>
      </div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
  return _stationIcon;
}

// ── EIM Logo ──

// ── Map fly-to on selection ──

function MapFocus({ center }) {
  const map = useMap();
  const prevCenter = useRef(null);
  useEffect(() => {
    if (!center) return;
    const same =
      prevCenter.current &&
      prevCenter.current[0] === center[0] &&
      prevCenter.current[1] === center[1];
    if (same) return;
    prevCenter.current = center;
    map.setView(center, 18, { animate: true, duration: 0.3 });
  }, [center, map]);
  return null;
}

// ── Detail Card ──

function DetailCard({ battery, onClose, viewerMode }) {
  if (!battery) return null;

  // Cross-links: open the SwapDash battery report focused on this BMS,
  // and the Urja Drishti degradation dashboard. Both live on
  // urjadrishti.eim.digital and use the bms_id as the deep-link key.
  // Hidden for viewer-mode accounts (live-data only, no history).
  const bms = encodeURIComponent(battery.battery_id);
  const historyUrl = `https://urjadrishti.eim.digital/battery-report?battery=${bms}`;
  const degradationUrl = `https://urjadrishti.eim.digital/#battery=${bms}`;

  return (
    <div className="detail-overlay">
      <div className="detail-header">
        <h2>
          {!battery.enneagon_plate_no && (
            <span
              className="unregistered-badge"
              title="Not present in the official battery registry"
              aria-label="Not in battery registry"
            >
              ⚠
            </span>
          )}
          {battery.battery_id}
        </h2>
        {battery.enneagon_plate_no ? (
          <div className="battery-plate-line">
            Serial {battery.enneagon_plate_no}
            {battery.bcs_capacity ? ` · ${battery.bcs_capacity}` : ""}
          </div>
        ) : (
          <div className="battery-plate-line" style={{ color: "#f3c87a" }}>
            Not present in the official battery registry
          </div>
        )}
        {!viewerMode && (
        <div className="detail-cross-links">
          <a className="cross-link" href={historyUrl} target="_blank" rel="noopener">
            ⚡ View Charging History
          </a>
          <a className="cross-link" href={degradationUrl} target="_blank" rel="noopener">
            📊 Degradation Stats
          </a>
        </div>
        )}
        <button className="detail-close" onClick={onClose}>
          &times;
        </button>
      </div>
      <div className="detail-body">
        <div className="detail-section">
          <div className="detail-section-title">Identity</div>
          <div className="detail-grid">
            <div className="detail-item">
              <div className="detail-item-label">BMS ID</div>
              <div className="detail-item-value small">
                {battery.battery_id}
              </div>
            </div>
            <div className="detail-item">
              <div className="detail-item-label">Battery Serial Number</div>
              <div className="detail-item-value small">
                {battery.enneagon_plate_no || "—"}
              </div>
            </div>
            <div className="detail-item">
              <div className="detail-item-label">BCS Capacity</div>
              <div className="detail-item-value small">
                {battery.bcs_capacity || "—"}
              </div>
            </div>
            <div className="detail-item">
              <div className="detail-item-label">Truck</div>
              <div className="detail-item-value small">
                {battery.associated_truck || "N/A"}
              </div>
            </div>
            <div className="detail-item">
              <div className="detail-item-label">Fleet</div>
              <div className="detail-item-value small">
                {battery.fleet_name || "N/A"}
              </div>
            </div>
            <div className="detail-item">
              <div className="detail-item-label">Location</div>
              <div className="detail-item-value small">
                {battery.station_name
                  ? `⚡ ${battery.station_name}`
                  : battery.associated_truck && battery.associated_truck.toLowerCase() !== "unknown"
                  ? "On Truck"
                  : "Off-station"}
                {battery.station_distance_m != null && (
                  <span style={{ color: "#898989", marginLeft: 6 }}>
                    ({battery.station_distance_m}m)
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="detail-section">
          <div className="detail-section-title">Charge</div>
          <div className="detail-grid">
            <div className="detail-item">
              <div className="detail-item-label">SOC</div>
              <div
                className={`detail-item-value color-${socColor(battery.soc)}`}
              >
                {fmt(battery.soc, "%")}
              </div>
            </div>
            <div className="detail-item">
              <div className="detail-item-label">SOH</div>
              <div
                className={`detail-item-value color-${sohColor(
                  battery.batt_soh
                )}`}
              >
                {fmt(battery.batt_soh, "%")}
              </div>
            </div>
            <div className="detail-item">
              <div className="detail-item-label">Voltage</div>
              <div className="detail-item-value">
                {fmt(battery.batt_volt, " V")}
              </div>
            </div>
            <div className="detail-item">
              <div className="detail-item-label">Current</div>
              <div className="detail-item-value">
                {fmt(battery.batt_cur, " A")}
              </div>
            </div>
          </div>
        </div>

        <div className="detail-section">
          <div className="detail-section-title">Temperature</div>
          <div className="detail-grid">
            <div className="detail-item">
              <div className="detail-item-label">Max Cell</div>
              <div className="detail-item-value">
                {fmt(battery.max_cell_temp, "°C")}
              </div>
            </div>
            <div className="detail-item">
              <div className="detail-item-label">Min Cell</div>
              <div className="detail-item-value">
                {fmt(battery.min_cell_temp, "°C")}
              </div>
            </div>
            <div className="detail-item full-width">
              <div className="detail-item-label">Avg Cell</div>
              <div className="detail-item-value">
                {fmt(battery.avg_cell_temp, "°C")}
              </div>
            </div>
          </div>
        </div>

        <div className="detail-section">
          <div className="detail-section-title">Power</div>
          <div className="detail-grid">
            <div className="detail-item">
              <div className="detail-item-label">Total Charge</div>
              <div className="detail-item-value">
                {fmt(battery.total_chg_pwr, " kWh")}
              </div>
            </div>
            <div className="detail-item">
              <div className="detail-item-label">Total Discharge</div>
              <div className="detail-item-value">
                {fmt(battery.total_dischg_pwr, " kWh")}
              </div>
            </div>
          </div>
        </div>

        <div className="detail-section">
          <div className="detail-section-title">Vehicle</div>
          <div className="detail-grid">
            <div className="detail-item">
              <div className="detail-item-label">Wheel Speed</div>
              <div className="detail-item-value">
                {fmt(battery.wheel_speed, " km/h")}
              </div>
            </div>
            <div className="detail-item">
              <div className="detail-item-label">GPS</div>
              <div className="detail-item-value small">
                {battery.gps_lat != null
                  ? `${Number(battery.gps_lat).toFixed(4)}, ${Number(
                      battery.gps_lng
                    ).toFixed(4)}`
                  : "N/A"}
              </div>
            </div>
          </div>
        </div>

        <div className="detail-section">
          <div className="detail-section-title">Timestamps</div>
          <div className="detail-grid">
            <div className="detail-item">
              <div className="detail-item-label">GPS Time</div>
              <div className="detail-item-value small">
                {fmtTime(battery.gps_time)}
              </div>
            </div>
            <div className="detail-item">
              <div className="detail-item-label">OBD Time</div>
              <div className="detail-item-value small">
                {fmtTime(battery.obd_end_time)}
              </div>
            </div>
            <div className="detail-item full-width">
              <div className="detail-item-label">Last Updated</div>
              <div className="detail-item-value small">
                {battery.updated_at
                  ? new Date(battery.updated_at).toLocaleString()
                  : "N/A"}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Multi-select filter section ──

function FilterSection({ title, options, selected, onToggle }) {
  if (!options || options.length === 0) return null;
  return (
    <div className="filter-section">
      <div className="filter-section-title">{title}</div>
      <div className="filter-chips">
        {options.map((opt) => (
          <button
            key={opt.value}
            className={`filter-chip ${selected.has(opt.value) ? "active" : ""}`}
            onClick={() => onToggle(opt.value)}
            title={opt.label}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── App ──

function App() {
  // Auth state is the user record returned by /api/auth/me (or /api/auth/login).
  // The cookie itself is httpOnly so we can't read it from JS — we hit
  // /api/auth/me on load to decide whether we're signed in. Bootstrap from
  // sessionStorage so a hard refresh doesn't blink to the login screen.
  const [user, setUser] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("eim_user") || "null"); }
    catch { return null; }
  });
  const [authChecked, setAuthChecked] = useState(!!user);
  useEffect(() => {
    let cancelled = false;
    fetch("/api/auth/me", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((u) => {
        if (cancelled) return;
        if (u && u.email) {
          setUser(u);
          try { sessionStorage.setItem("eim_user", JSON.stringify(u)); } catch {}
        } else if (user) {
          setUser(null);
          try { sessionStorage.removeItem("eim_user"); } catch {}
        }
        setAuthChecked(true);
      })
      .catch(() => setAuthChecked(true));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const token = user ? "cookie" : null; // sentinel so existing token gates still work
  const [batteries, setBatteries] = useState([]);
  const [stations, setStations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mapMode, setMapMode] = useState("satellite");
  const [mobileView, setMobileView] = useState("list"); // "list" | "map"
  const [search, setSearch] = useState("");
  const [fleetFilter, setFleetFilter] = useState(() => new Set());
  const [socFilter, setSocFilter] = useState(() => new Set());
  const [sohFilter, setSohFilter] = useState(() => new Set());
  const [tempFilter, setTempFilter] = useState(() => new Set());
  const [dischargeFilter, setDischargeFilter] = useState(() => new Set());
  const [locationFilter, setLocationFilter] = useState(() => new Set()); // 'station' | 'truck' | 'unlinked'
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [colorBy, setColorBy] = useState(() => {
    try { return localStorage.getItem("bd_colorBy") || "auto"; } catch { return "auto"; }
  });
  useEffect(() => {
    try { localStorage.setItem("bd_colorBy", colorBy); } catch {}
  }, [colorBy]);

  const toggleSet = (setter) => (val) => {
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(val)) next.delete(val);
      else next.add(val);
      return next;
    });
  };

  const clearAllFilters = () => {
    setSearch("");
    setFleetFilter(new Set());
    setSocFilter(new Set());
    setSohFilter(new Set());
    setTempFilter(new Set());
    setDischargeFilter(new Set());
    setLocationFilter(new Set());
  };

  const activeFilterCount =
    (search ? 1 : 0) +
    fleetFilter.size +
    socFilter.size +
    sohFilter.size +
    tempFilter.size +
    dischargeFilter.size +
    locationFilter.size;

  const logout = async () => {
    try { await fetch("/api/auth/logout", { method: "POST", credentials: "include" }); } catch {}
    try { sessionStorage.removeItem("eim_user"); } catch {}
    setUser(null);
  };

  const selectedRef = useRef(null);
  selectedRef.current = selected;

  const fetchBatteries = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(API, { credentials: "include" });
      if (res.status === 401) {
        logout();
        return;
      }
      const data = await res.json();
      const unique = dedup(data);
      setBatteries(unique);
      if (selectedRef.current) {
        const key = String(selectedRef.current.battery_id).trim();
        const updated = unique.find((b) => b.battery_id === key);
        if (updated) setSelected(updated);
      }
    } catch (err) {
      console.error("Fetch failed:", err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchBatteries();
    const interval = setInterval(fetchBatteries, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchBatteries]);

  // Swap stations are dynamic config from the server; fetch when signed in.
  useEffect(() => {
    if (!token) return;
    fetch("/api/swap-stations", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setStations(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [token]);

  const select = (b) => {
    setSelected((prev) =>
      prev && prev.battery_id === b.battery_id ? null : b
    );
  };

  const exportExcel = () => {
    const rows = filteredBatteries.map((b) => ({
      "BMS ID": b.battery_id,
      "Battery Serial Number": b.enneagon_plate_no || "",
      "BCS Capacity": b.bcs_capacity || "",
      "Fleet Owner": b.fleet_name || "",
      "Location": b.station_name || (b.associated_truck && b.associated_truck.toLowerCase() !== "unknown" ? "On Truck" : "Off-station"),
      "Station": b.station_name || "",
      "Truck": b.associated_truck || "",
      "SOC (%)": b.soc != null ? Number(b.soc).toFixed(1) : "",
      "SOH (%)": b.batt_soh != null ? Number(b.batt_soh).toFixed(1) : "",
      "Voltage (V)": b.batt_volt != null ? Number(b.batt_volt).toFixed(1) : "",
      "Current (A)": b.batt_cur != null ? Number(b.batt_cur).toFixed(1) : "",
      "Max Cell Temp (°C)": b.max_cell_temp != null ? Number(b.max_cell_temp).toFixed(1) : "",
      "Min Cell Temp (°C)": b.min_cell_temp != null ? Number(b.min_cell_temp).toFixed(1) : "",
      "Avg Cell Temp (°C)": b.avg_cell_temp != null ? Number(b.avg_cell_temp).toFixed(1) : "",
      "Wheel Speed (km/h)": b.wheel_speed != null ? Number(b.wheel_speed).toFixed(1) : "",
      "Total Charge (kWh)": b.total_chg_pwr != null ? Number(b.total_chg_pwr).toFixed(1) : "",
      "Total Discharge (kWh)": b.total_dischg_pwr != null ? Number(b.total_dischg_pwr).toFixed(1) : "",
      "Latitude": b.gps_lat != null ? Number(b.gps_lat).toFixed(6) : "",
      "Longitude": b.gps_lng != null ? Number(b.gps_lng).toFixed(6) : "",
      "GPS Time": b.gps_time ? fmtTime(b.gps_time) : "",
      "OBD Time": b.obd_end_time ? fmtTime(b.obd_end_time) : "",
      "Last Updated": b.updated_at ? new Date(b.updated_at).toLocaleString() : "",
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Battery Status");
    XLSX.writeFile(wb, `battery_status_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const fleetOptions = useMemo(
    () =>
      Array.from(new Set(batteries.map((b) => b.fleet_name).filter(Boolean))).sort(
        (a, b) => a.localeCompare(b)
      ),
    [batteries]
  );

  const filteredBatteries = useMemo(() => {
    const s = search.trim().toLowerCase();
    return batteries.filter((b) => {
      if (s) {
        const hit =
          (b.battery_id && String(b.battery_id).toLowerCase().includes(s)) ||
          (b.enneagon_plate_no && String(b.enneagon_plate_no).toLowerCase().includes(s)) ||
          (b.associated_truck && String(b.associated_truck).toLowerCase().includes(s)) ||
          (b.fleet_name && b.fleet_name.toLowerCase().includes(s));
        if (!hit) return false;
      }
      if (fleetFilter.size > 0 && !fleetFilter.has(b.fleet_name || "__none__")) return false;
      if (socFilter.size > 0 && !socFilter.has(socBucket(b.soc))) return false;
      if (sohFilter.size > 0 && !sohFilter.has(sohBucket(b.batt_soh))) return false;
      if (tempFilter.size > 0 && !tempFilter.has(tempBucket(b.max_cell_temp))) return false;
      if (dischargeFilter.size > 0 && !dischargeFilter.has(dischargeBucket(b.total_dischg_pwr))) return false;
      if (locationFilter.size > 0) {
        const loc = b.station_id
          ? "station"
          : (b.associated_truck && b.associated_truck.toLowerCase() !== "unknown" ? "truck" : "unlinked");
        if (!locationFilter.has(loc)) return false;
      }
      return true;
    });
  }, [batteries, search, fleetFilter, socFilter, sohFilter, tempFilter, dischargeFilter, locationFilter]);

  if (!authChecked) {
    return <div className="loading">Checking session…</div>;
  }
  if (!user) {
    return <Login onLogin={(u) => {
      setUser(u);
      try { sessionStorage.setItem("eim_user", JSON.stringify(u)); } catch {}
    }} />;
  }

  if (loading) {
    return <div className="loading">Loading fleet data...</div>;
  }

  const groups = groupByLocation(filteredBatteries);
  const groupEntries = sortedGroupEntries(groups);
  const withGps = filteredBatteries.filter(
    (b) => b.gps_lat != null && b.gps_lng != null
  );

  const mapCenter = (() => {
    if (withGps.length === 0) return [20.5937, 78.9629];
    const avgLat =
      withGps.reduce((s, b) => s + Number(b.gps_lat), 0) / withGps.length;
    const avgLng =
      withGps.reduce((s, b) => s + Number(b.gps_lng), 0) / withGps.length;
    return [avgLat, avgLng];
  })();

  const flyTarget =
    selected && selected.gps_lat != null
      ? [Number(selected.gps_lat), Number(selected.gps_lng)]
      : null;

  // Auto = follow the most-prominent active filter; explicit choice overrides.
  const effectiveColorBy = (() => {
    if (colorBy !== "auto") return colorBy;
    if (sohFilter.size > 0) return "soh";
    if (tempFilter.size > 0) return "temp";
    if (dischargeFilter.size > 0) return "discharge";
    if (socFilter.size > 0) return "soc";
    return "soc";
  })();
  const colorByFn = BATTERY_COLOR_BY[effectiveColorBy]?.fn || BATTERY_COLOR_BY.soc.fn;

  return (
    <div className="dashboard">
      {/* ── Mobile Tab Bar ── */}
      <div className="mobile-tabs">
        <button
          className={mobileView === "list" ? "active" : ""}
          onClick={() => setMobileView("list")}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" />
            <line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
          </svg>
          List
        </button>
        <button
          className={mobileView === "map" ? "active" : ""}
          onClick={() => setMobileView("map")}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" />
            <line x1="8" y1="2" x2="8" y2="18" /><line x1="16" y1="6" x2="16" y2="22" />
          </svg>
          Map
        </button>
      </div>

      {/* ── Sidebar ── */}
      <div className={`sidebar ${mobileView === "map" ? "mobile-hidden" : ""}`}>
        <div className="sidebar-header">
          <div className="sidebar-brand">
            <div className="sidebar-logo">
              <img src="/eim_logo.svg" alt="Energy in Motion" />
            </div>
            <h1>
              EIM <span className="brand-accent">·</span> Battery
            </h1>
            <ProfileChip currentPortal="battery_dashboard" onLogout={logout} compact />
            <button className="logout-btn" onClick={logout} title="Sign out">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
            </button>
          </div>
          <div className="sidebar-stats">
            <div className="sidebar-stat">
              <strong>{batteries.length}</strong> Batteries
            </div>
            <div className="sidebar-stat">
              <strong>{Object.values(groups).filter((g) => g.kind === "truck").length}</strong> Trucks
            </div>
            <div className="sidebar-stat">
              <strong>{batteries.filter((b) => b.station_id).length}</strong> In Station
            </div>
            <button className="export-btn" onClick={exportExcel} title="Export to Excel">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Export
            </button>
          </div>
          <div className="sidebar-search">
            <input
              type="text"
              placeholder="Search battery, truck, fleet..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="filter-bar">
            <button
              className={`filter-toggle ${filtersOpen ? "open" : ""}`}
              onClick={() => setFiltersOpen((v) => !v)}
            >
              Filters{activeFilterCount > 0 ? ` · ${activeFilterCount}` : ""}
              <span className="filter-toggle-caret">{filtersOpen ? "▴" : "▾"}</span>
            </button>
            {activeFilterCount > 0 && (
              <button className="filter-clear" onClick={clearAllFilters}>
                Clear
              </button>
            )}
            <div className="filter-result-count">
              {filteredBatteries.length} / {batteries.length}
            </div>
          </div>
          {filtersOpen && (
            <div className="filter-panel">
              <FilterSection
                title="Location"
                options={[
                  { value: "station", label: `In Station (${batteries.filter((b) => b.station_id).length})` },
                  { value: "truck", label: `On Truck (${batteries.filter((b) => !b.station_id && b.associated_truck && b.associated_truck.toLowerCase() !== "unknown").length})` },
                  { value: "unlinked", label: `Off-station Unlinked (${batteries.filter((b) => !b.station_id && (!b.associated_truck || b.associated_truck.toLowerCase() === "unknown")).length})` },
                ]}
                selected={locationFilter}
                onToggle={toggleSet(setLocationFilter)}
              />
              <FilterSection
                title="Fleet"
                options={fleetOptions.map((f) => ({ value: f, label: f }))}
                selected={fleetFilter}
                onToggle={toggleSet(setFleetFilter)}
              />
              <FilterSection
                title="SOC"
                options={SOC_OPTIONS}
                selected={socFilter}
                onToggle={toggleSet(setSocFilter)}
              />
              <FilterSection
                title="SOH"
                options={SOH_OPTIONS}
                selected={sohFilter}
                onToggle={toggleSet(setSohFilter)}
              />
              <FilterSection
                title="Max Cell Temp"
                options={TEMP_OPTIONS}
                selected={tempFilter}
                onToggle={toggleSet(setTempFilter)}
              />
              <FilterSection
                title="Total Discharge"
                options={DISCHARGE_OPTIONS}
                selected={dischargeFilter}
                onToggle={toggleSet(setDischargeFilter)}
              />
            </div>
          )}
        </div>
        <div className="battery-list">
          {groupEntries.map(([key, group]) => (
            <div className="truck-group" key={key}>
              <div className={`truck-label group-${group.kind}`}>
                {group.kind === "station" ? (
                  <>
                    <span className="station-icon" aria-hidden>⚡</span>
                    <span className="station-name">{group.station_name}</span>
                    <span className="station-count">{group.items.length} bat.</span>
                  </>
                ) : group.kind === "unlinked" ? (
                  <span className="unlinked-name">{group.label}</span>
                ) : (
                  <>
                    {group.fleet && <span className="truck-fleet">{group.fleet}</span>}
                    <span className="truck-plate">{group.truck}</span>
                  </>
                )}
              </div>
              {group.items.map((b) => (
                <div
                  key={b.battery_id}
                  className={`battery-card ${
                    selected?.battery_id === b.battery_id ? "selected" : ""
                  }`}
                  onClick={() => select(b)}
                >
                  <div className="battery-card-top">
                    {!b.enneagon_plate_no && (
                      <span
                        className="unregistered-badge"
                        title="Not present in the official battery registry"
                        aria-label="Not in battery registry"
                      >
                        ⚠
                      </span>
                    )}
                    <span className="battery-id">{b.battery_id}</span>
                    <span
                      className={`battery-location-dot ${
                        b.gps_lat == null ? "no-gps" : ""
                      }`}
                    />
                  </div>
                  {(b.enneagon_plate_no || b.bcs_capacity) && (
                    <div
                      className="battery-plate-meta"
                      style={{
                        fontSize: 11,
                        color: "#6b7574",
                        marginTop: 4,
                        marginBottom: 12,
                        display: "flex",
                        gap: 6,
                        flexWrap: "wrap",
                      }}
                    >
                      {b.enneagon_plate_no && (
                        <span title="Battery Serial Number">
                          ⬢ {b.enneagon_plate_no}
                        </span>
                      )}
                      {b.bcs_capacity && (
                        <span title="BCS capacity">{b.bcs_capacity}</span>
                      )}
                    </div>
                  )}
                  <div className="battery-metrics">
                    <div className="metric">
                      <div className="metric-label">SOC</div>
                      <div
                        className={`metric-value color-${socColor(b.soc)}`}
                      >
                        {b.soc != null
                          ? `${Number(b.soc).toFixed(0)}%`
                          : "—"}
                      </div>
                      <div className="metric-bar">
                        <div
                          className={`metric-bar-fill fill-${socColor(b.soc)}`}
                          style={{ width: `${b.soc ?? 0}%` }}
                        />
                      </div>
                    </div>
                    <div className="metric">
                      <div className="metric-label">SOH</div>
                      <div
                        className={`metric-value color-${sohColor(
                          b.batt_soh
                        )}`}
                      >
                        {b.batt_soh != null
                          ? `${Number(b.batt_soh).toFixed(0)}%`
                          : "—"}
                      </div>
                      <div className="metric-bar">
                        <div
                          className={`metric-bar-fill fill-${sohColor(
                            b.batt_soh
                          )}`}
                          style={{ width: `${b.batt_soh ?? 0}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* ── Map ── */}
      <div className={`map-area ${mobileView === "list" ? "mobile-hidden" : ""}`}>
        <div className="map-toggle">
          <button
            className={mapMode === "light" ? "active" : ""}
            onClick={() => setMapMode("light")}
          >
            Map
          </button>
          <button
            className={mapMode === "satellite" ? "active" : ""}
            onClick={() => setMapMode("satellite")}
          >
            Satellite
          </button>
        </div>
        <div className="map-colorby" title="Marker color by">
          <label>Color by</label>
          <select value={colorBy} onChange={(e) => setColorBy(e.target.value)}>
            <option value="auto">Auto (follow filter)</option>
            <option value="soc">SOC</option>
            <option value="soh">SOH</option>
            <option value="temp">Max Cell Temp</option>
            <option value="discharge">Total Discharge</option>
          </select>
          {colorBy === "auto" && (
            <span className="map-colorby-resolved">
              {BATTERY_COLOR_BY[effectiveColorBy]?.label || "SOC"}
            </span>
          )}
        </div>
        <MapContainer center={mapCenter} zoom={5} zoomControl={false}>
          <ZoomControl position="bottomright" />
          {mapMode === "light" ? (
            <TileLayer
              key="light"
              url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
              attribution='&copy; <a href="https://carto.com/">CARTO</a>'
              maxZoom={20}
            />
          ) : (
            <TileLayer
              key="satellite"
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              attribution="&copy; Esri"
              maxZoom={19}
            />
          )}
          <MapFocus center={flyTarget} />
          {stations.map((s) => (
            <Marker
              key={`station-${s.id}`}
              position={[Number(s.lat), Number(s.lng)]}
              icon={createStationIcon()}
              zIndexOffset={-100}
            >
              <Tooltip direction="top" offset={[0, -14]} opacity={1} permanent={false}>
                <div className="station-tooltip">
                  <div className="station-tooltip-name">⚡ {s.name}</div>
                  {s.region && <div className="station-tooltip-meta">{s.region}</div>}
                </div>
              </Tooltip>
            </Marker>
          ))}
          {withGps.map((b) => (
            <Marker
              key={b.battery_id}
              position={[Number(b.gps_lat), Number(b.gps_lng)]}
              icon={createMarkerIcon(
                colorByFn(b),
                selected?.battery_id === b.battery_id
              )}
              eventHandlers={{ click: () => select(b) }}
            />
          ))}
        </MapContainer>

        {selected && (
          <DetailCard battery={selected} onClose={() => setSelected(null)} viewerMode={!!(user && user.viewer_mode)} />
        )}
      </div>
    </div>
  );
}

export default App;
